import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'react-use';

import { animated, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import { debounce } from 'lodash';

import SearchIcon from '@/components/Icon/Search';

import Option from './Option';

const getOptionLabel = (value: string, option: string) => {
  const label = option.replaceAll(value, `<span class="text-wk-words-1">${value}</span>`);
  return <p dangerouslySetInnerHTML={{ __html: label }} className="text-[#8D8FA6]" />;
};

interface OptionType {
  label: ReactNode;
  value: string;
}

interface Props {
  width?: string | number;
  backgroundColor?: string;
  value?: string;
  defaultCanInput?: boolean;
  className?: string;
  onChange?: (value: string) => void;
  onSearch?: (value?: string) => void;
  onSelect?: (value: string) => void;
  getSearchOptions?: (value: string) => Promise<string[]>;
}

const Search: FC<Props> = ({
  width,
  onChange,
  onSearch,
  onSelect,
  className,
  value,
  getSearchOptions,
  backgroundColor,
  defaultCanInput = false,
}) => {
  const { t } = useTranslation('resources');
  const [canInput, setCanInput] = useState(defaultCanInput || !!value);
  const [optionsVisible, setOptionsVisible] = useState(false);
  const [options, setOptions] = useState<OptionType[]>([]);
  const selectRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const styles = useSpring({
    opacity: optionsVisible ? 1 : 0,
    y: optionsVisible ? 0 : 24,
    display: optionsVisible ? 'block' : 'none',
    delay: (key) => (key === 'display' ? 200 : 0),
    boxShadow: '0px 8px 30px 0px rgba(0, 0, 0, 0.06)',
  });

  const handleClickOutside = (event: any) => {
    if (selectRef.current && !selectRef.current.contains(event.target)) {
      setOptionsVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useUpdateEffect(() => {
    if (canInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [canInput]);

  const getOptions = useCallback(
    debounce((value: string) => {
      if (!getSearchOptions) {
        return;
      }
      if (!value) {
        setOptions([]);
        setOptionsVisible(false);
        return;
      }
      getSearchOptions?.(value).then((options) => {
        if (!options.length) {
          setOptions([]);
          setOptionsVisible(false);
          return;
        }
        const optionList = options.map((option) => ({ label: getOptionLabel(value, option), value: option }));
        setOptions(optionList);
        setOptionsVisible(true);
      });
    }, 500),
    [],
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    onChange?.(value);
    getOptions(value);
  };

  const handleOptionChange = (value: string) => {
    onChange?.(value);
    onSelect?.(value);
    setOptionsVisible(false);
  };

  const handleSearch = useCallback(
    debounce(() => {
      setOptionsVisible(false);
      // if (!value) {
      //   return;
      // }
      onSearch?.(value);
    }, 500),
    [value],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  return canInput ? (
    <div
      ref={selectRef}
      style={{ width, backgroundColor }}
      className={classNames(
        'relative h-[43px] rounded-[99px] border-[1px] border-solid border-[#F0F1FC] bg-white pl-[16px] pr-[5px] xl:pl-[24px]',
        className,
      )}
    >
      <search className="h-full w-full">
        <form onSubmit={handleSubmit} className="inline-flex h-full w-full items-center justify-between">
          <input
            ref={inputRef}
            value={value}
            onChange={handleChange}
            className="box-border h-full flex-1 border-0 text-[#1B1F4D] placeholder-[#8D8FA6] focus:outline-0"
            placeholder={t('请输入搜索内容')}
            style={{ backgroundColor }}
            type="search"
          />
          <button
            type="submit"
            className="ml-[16px] min-w-[54px] rounded-[999px] bg-[#FF5353] px-[8px] py-[6px] text-[12px] font-semibold text-white lg:px-[12px] lg:text-[14px] xl:ml-[24px]"
          >
            {t('搜索')}
          </button>
        </form>
      </search>
      <animated.ul
        className="absolute right-0 top-[45px] z-[100] max-h-[300px] w-[calc(100vw-40px)] overflow-y-auto rounded-[12px] bg-white py-[8px]	lg:w-full"
        style={styles}
      >
        {options.map(({ value, label }) => (
          <Option key={value} value={value} onChange={handleOptionChange}>
            {label}
          </Option>
        ))}
      </animated.ul>
    </div>
  ) : (
    <div
      onClick={() => setCanInput(true)}
      style={{ width, backgroundColor }}
      className={classNames(
        'inline-flex h-[43px] cursor-pointer items-center rounded-[99px] border-[1px] border-solid border-[#F0F1FC] bg-white px-[16px] xl:px-[24px]',
        className,
      )}
    >
      <SearchIcon />
      <span className="ml-[10px] text-[#1B1F4D]">{t('搜索')}</span>
    </div>
  );
};

export default Search;
